import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Card, CardContent, Grid, Chip
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  CheckCircle,
} from '@mui/icons-material';
import { getSignupEnabled } from '../api';

const tiers = [
  {
    name: 'Starter',
    price: '$39',
    priceSubtext: '/month + $5/employee',
    highlight: false,
    limits: 'Up to 25 employees',
    example: 'Example: 10 employees = $89/mo',
    features: [
      'Time tracking & clock in/out',
      'Employee management',
      'PTO & leave tracking',
      'Payroll calculations & pay stubs',
      'Basic reports',
      'Single company',
      'Email support',
    ],
    cta: 'Start 14-Day Free Trial',
    ctaVariant: 'outlined',
  },
  {
    name: 'Business',
    price: '$39',
    priceSubtext: '/month + $5/employee',
    highlight: true,
    limits: 'Unlimited employees',
    example: 'Example: 25 employees = $164/mo',
    features: [
      'Everything in Starter, plus:',
      'Invoice pipeline & Zoho sync',
      'Multi-company support',
      'Custom roles & permissions',
      'Manager approval workflows',
      'Report builder & scheduled reports',
      'SSO (Entra ID / OIDC)',
      'Priority email support',
    ],
    cta: 'Start 14-Day Free Trial',
    ctaVariant: 'contained',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    priceSubtext: 'contact us',
    highlight: false,
    limits: 'Unlimited everything',
    example: 'Volume discounts available',
    features: [
      'Everything in Business, plus:',
      'Tax filing (941/940/W-2)',
      'NACHA direct deposit (coming soon)',
      'Dedicated account manager',
      'Custom integrations',
      'SLA guarantee',
      'On-premise deployment option',
      'Phone support',
    ],
    cta: 'Contact Sales',
    ctaVariant: 'outlined',
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  // When self-serve signup is off, every CTA routes to the demo-request form
  // instead of the (disabled) /signup page.
  const [signupEnabled, setSignupEnabled] = useState(false);

  useEffect(() => {
    getSignupEnabled()
      .then(res => setSignupEnabled(!!res.data.enabled))
      .catch(() => setSignupEnabled(false));
  }, []);

  const handleCTA = (tier) => {
    if (tier.name === 'Enterprise' || !signupEnabled) {
      navigate('/preview#demo-request');
    } else {
      navigate('/signup');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a2e', color: 'white', py: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/preview')}
            >
              <ClockIcon sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">HourTrax</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined" size="small"
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                component={Link} to="/preview"
              >
                Home
              </Button>
              <Button
                variant="contained" size="small"
                onClick={() => navigate(signupEnabled ? '/signup' : '/preview#demo-request')}
              >
                {signupEnabled ? 'Sign Up' : 'Request Demo'}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pricing Header */}
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Simple, Transparent Pricing
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Built for staffing agencies. Priced to compete.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Every plan includes a 14-day free trial with full features. No credit card required.
        </Typography>
      </Container>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ pb: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          {tiers.map((tier) => (
            <Grid item xs={12} md={4} key={tier.name}>
              <Card
                elevation={tier.highlight ? 8 : 2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: tier.highlight ? '2px solid' : '1px solid',
                  borderColor: tier.highlight ? 'primary.main' : 'divider',
                  position: 'relative',
                  transform: tier.highlight ? 'scale(1.05)' : 'none',
                }}
              >
                {tier.highlight && (
                  <Chip
                    label="Most Popular"
                    color="primary" size="small"
                    sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}
                  />
                )}
                <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {tier.name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h3" fontWeight="bold" component="span">
                      {tier.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                      {tier.priceSubtext}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {tier.limits}
                  </Typography>
                  {tier.example && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block', fontStyle: 'italic' }}>
                      {tier.example}
                    </Typography>
                  )}

                  <Box sx={{ flex: 1, mb: 3 }}>
                    {tier.features.map((feature, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        {i === 0 && feature.startsWith('Everything') ? (
                          <Typography variant="body2" fontWeight="bold" color="primary">{feature}</Typography>
                        ) : (
                          <>
                            <CheckCircle fontSize="small" color="success" sx={{ mt: 0.2 }} />
                            <Typography variant="body2">{feature}</Typography>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Button
                    variant={tier.ctaVariant}
                    color="primary" fullWidth size="large"
                    onClick={() => handleCTA(tier)}
                    sx={{ py: 1.5 }}
                  >
                    {signupEnabled || tier.name === 'Enterprise' ? tier.cta : 'Request a Demo'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: 'white', py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {[
              { q: 'How long is the free trial?', a: '14 days with full access to all features. No credit card required to start.' },
              { q: 'Can I change plans later?', a: 'Yes, upgrade or downgrade anytime. Changes take effect on your next billing cycle.' },
              { q: 'What happens when my trial ends?', a: 'Your account goes read-only — you can still view and export all your data, but new time entries and payroll runs are paused until you subscribe. No data is ever deleted.' },
              { q: 'Is my data secure?', a: 'Yes. All data is encrypted in transit and at rest. Each tenant has fully isolated data. We use industry-standard authentication with optional SSO.' },
              { q: 'Do you support direct deposit?', a: 'NACHA file generation for ACH direct deposit is coming soon on the Enterprise plan. Currently, HourTrax handles payroll calculations and pay stub generation.' },
              { q: 'Can I import data from my current provider?', a: 'Yes. HourTrax supports mid-year payroll cutover with YTD import from ADP, Gusto, Paychex, QuickBooks, and more — including automatic pay stub scanning.' },
            ].map(({ q, a }, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{q}</Typography>
                <Typography variant="body2" color="text.secondary">{a}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a1a2e', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ClockIcon sx={{ mr: 1 }} />
                <Typography variant="h6">HourTrax</Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Professional Time Tracking & Payroll Management
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                &copy; {new Date().getFullYear()} HourTrax&trade;
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
